"use client";

import { useCallback } from "react";
import { sha1 } from "js-sha1";
import { sha256 } from "js-sha256";

import { STORAGE_KEYS } from "~/types/Storage";
import { useWaitListService } from "~/services/api/services/auth";
import { useUnauthorizedEventService } from "~/services/api/services/event";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { sendFacebookEvent } from "~/lib/facebookEvents/FacebookEventsClient";
import { getStorageItem, setStorageItem } from "~/lib/storage/Storage";
import useUserId from "~/hooks/useUserId";
import { QUIZ_EVENTS } from "../events";
import { useUpdateKlaviyoUserProfile } from "~/lib/klaviyo/hooks/useUpdateKlaviyoUserProfile";
import { useSubscribeKlaviyoUserForMarketing } from "~/lib/klaviyo/hooks/useSubscribeKlaviyoUserForMarketing";
import { TREATMENT, TREATMENTS } from "~/constants/treatments";
import { resolveQuizTracking } from "../tracking";
import { trackFxEvent } from "~/lib/optimizely/trackFxEvent";
import { FX_EVENT_NAMES } from "~/lib/optimizely/fxEvents";
import { type WaitlistData } from "../waitlist";
import { COMPLETION_WAITLIST_PREFIX } from "../constants";
import { sendPinterestEvent } from "~/lib/pinterestEvents/PinterestEventsClient";
import { sendTikTokEvent } from "~/lib/tiktokEvents/TikTokEventsClient";

const WAIT_LIST_TYPES = {
    start_after_porstpartum_waitlist: "pregnancy",
    the_minimum_age_to_begin_waitlist: "age",
    we_do_not_currently_subscribe_waitlist: "state",
    enter_you_information: "quiz_completion",
    enter_you_information_2: "quiz_completion",
    waitlist_under_21: "age",
    wait_until_postpartum: "pregnancy",
};

export const ANSWERS_WITH_GOALS: Record<string, TREATMENT> = {
    "I have Endometriosis, or suspect that I do": TREATMENTS.ENDOMETRIOSIS,
    "I have Polycystic Ovarian Syndrome (PCOS)": TREATMENTS.PCOS,
    "I am experiencing postpartum symptoms": TREATMENTS.POSTPARTUM,
    "I am perimenopausal": TREATMENTS.PERIMENOPAUSE,
    "I am menopausal": TREATMENTS.MENOPAUSE,
};

const useWaitlist = (category: string, surveyID: string, isOestra = false) => {
    const postWaitList = useWaitListService();
    const { userId } = useUserId();

    const sendEvent = useTrackEvent();
    const sendBigQueryEvent = useUnauthorizedEventService();

    const updateKlaviyoUser = useUpdateKlaviyoUserProfile();
    const subscribeKlaviyoUserForMarketing = useSubscribeKlaviyoUserForMarketing();

    return useCallback(async (waitList: string, value: WaitlistData) => {
        const widget = QUIZ_EVENTS[category]?.widget || "questionaire";

        const meta_data = {
            quiz_id: surveyID,
            user_email: value.email,
            user_phone: value.phone,
            user_first_name: value.first_name,
            user_last_name: value.last_name,
            marketing_consent_checkbox: Boolean(value.consent),
            additional_info: {
                user_email_hashed_sha1: sha1(value.email || ""),
                user_email_hashed_sha256: sha256(value.email || ""),
                user_phone_hashed_sha1: sha1(value.phone || ""),
                user_phone_hashed_sha256: sha256(value.phone || ""),
                type: waitList && WAIT_LIST_TYPES[waitList],
            },
        };

        if (value.email || value.phone) {
            sendEvent({
                event: "user_interaction",
                value: {
                    event_name: "created_lead_email",
                    widget,
                    meta_data,
                },
            });
        }

        // Only a finished quiz counts as completed. Every other waitlist question is an exit, with or without an email
        const isQuizCompletion = !waitList || waitList.startsWith(COMPLETION_WAITLIST_PREFIX);

        // A skipped exit has no contact details, so there is nothing to report as a submit
        if (isQuizCompletion || value.email || value.phone) {
            sendFacebookEvent(sendBigQueryEvent, {
                event_name: "submit",
                email: value.email,
                phone: value.phone,
                firstName: value.first_name,
                lastName: value.last_name,
            });
        }

        if (!isQuizCompletion) {
            if (!value.email) return null;

            return postWaitList({
                name: userId || "",
                email: value.email,
            });
        }

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: "quiz_completed",
                widget,
                meta_data: {
                    quiz_id: surveyID,
                    click_text: value.click_text,
                    user_email: value.email,
                    user_phone: value.phone,
                    user_first_name: value.first_name,
                    user_last_name: value.last_name,
                    additional_info: {
                        user_email_hashed_sha1: value.email ? sha1(value.email) : "",
                        user_email_hashed_sha256: value.email ? sha256(value.email) : "",
                        user_phone_hashed_sha1: value.phone ? sha1(value.phone) : "",
                        user_phone_hashed_sha256: value.phone ? sha256(value.phone) : "",
                        quiz: JSON.stringify(value.quiz),
                    },
                },
            },
        });

        trackFxEvent({
            event_name: FX_EVENT_NAMES.QUIZ_COMPLETED,
            properties: { itemSlug: resolveQuizTracking(category).productName },
        });

        if (value.email && value.quiz) {
            const interestedProduct = getStorageItem(STORAGE_KEYS.INTERESTED_PRODUCT);

            const symptoms = value.quiz["select_symptoms"] ?? [];
            const goalType = value.quiz["what_brings_you_to_inner_balance"];
            const productGoal = goalType ? ANSWERS_WITH_GOALS[goalType as string] : undefined;

            void updateKlaviyoUser(value.email, {
                quiz_completed: "true",
                what_brings_you_here: goalType,
                symptoms: symptoms.join(),
                most_interested_product: interestedProduct || "unknown",
                product_goal: productGoal ?? "",
            }).then(() => {
                const phone = value.consent ? value.phone : "";
                void subscribeKlaviyoUserForMarketing(value.email, phone);
            });
        }

        const { pixels, productName: product_name } = resolveQuizTracking(category);

        sendFacebookEvent(sendBigQueryEvent, {
            event_name: "Offside",
            email: value.email,
            phone: value.phone,
            first_name: value.first_name,
            last_name: value.last_name,
        }, pixels);

        sendFacebookEvent(sendBigQueryEvent, {
            event_name: "QuizCompleted",
            email: value.email,
            phone: value.phone,
            first_name: value.first_name,
            last_name: value.last_name,
        });

        if (product_name !== "") {
            sendPinterestEvent(sendBigQueryEvent, {
                event_name: "addtocart",
                email: value.email,
                phone: value.phone,
                first_name: value.first_name,
                last_name: value.last_name,
                custom_data: {
                    line_items: [
                        {
                            product_name: product_name,
                        }
                    ]
                }
            });
        }

        sendTikTokEvent(sendBigQueryEvent, {
            event_name: "Subscribe",
            email: value.email,
            phone: value.phone,
            external_id: userId,
        });

        const json = JSON.stringify({
            email: value.email,
            phone: value.phone,
            first_name: value.first_name,
            last_name: value.last_name,
        });
        setStorageItem((isOestra ? "" : category) + STORAGE_KEYS.QUIZ_DATA, json);

        return Promise.resolve(null);
    }, [sendBigQueryEvent, postWaitList, sendEvent, updateKlaviyoUser, subscribeKlaviyoUserForMarketing, userId, category, surveyID, isOestra]);
};

export default useWaitlist;
